var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { property, queryAssignedNodes } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import styles from './fieldset.styles';
/**
 * @slot default - The component content
 * @slot error - error slot
 * @slot field-note - default field-note
 * @slot warning - warningNote slot
 *
 * @csspart base - The component's base wrapper.
 * @csspart legend - The component's legend element.
 * @csspart body - The component's body field.
 * @csspart list- The component's list field.
 * @csspart part-required- The component's required element.
 * @csspart part-default - The component's default slot.
 * @csspart loader - The component's skeleton loader element.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart warning-note- The component's warning-note wrapper.
 * @csspart part-warning - The component's warning slot.
 * @csspart warning-icon - The component's warning icon.
 * @csspart error-note- The component's error-note wrapper.
 * @csspart part-error - The component's error slot.
 * @csspart error-icon - The component's error icon.
 */
export class BlocksFieldset extends BlocksElement {
    constructor() {
        super(...arguments);
        /**
         * Name or Title for the warning icon
         * meant for accessibility purposes only
         */
        this.warningIconTitle = 'warning icon';
        /**
         * Name or Title for the error icon
         * meant for accessibility purposes only
         */
        this.errorIconTitle = 'error icon';
        this.slots = new SlotController(this, null, 'error', 'warning', 'field-note');
    }
    static { this.styles = styles; }
    get hasFieldNoteSlot() {
        return this.slots.hasSlotted('field-note');
    }
    get hasErrorNoteSlot() {
        return this.slots.hasSlotted('error');
    }
    get hasWarningNoteSlot() {
        return this.slots.hasSlotted('warning');
    }
    firstUpdated() {
        if (this.querySelector('cat-checkbox-field')) {
            // Set the checkbox role to list
            this.fieldsetRole = 'list';
            this.checkboxFieldItems?.forEach((checkboxFieldItems) => {
                checkboxFieldItems.roleForCheckboxField = 'listitem';
            });
        }
        // Set the radio group role to radiogroup
        if (this.querySelector('cat-radio-field')) {
            this.fieldsetRole = 'radiogroup';
        }
    }
    setAriaDescribedBy() {
        if (this.hasFieldNote()) {
            this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
        }
    }
    /**
     * Display error note from slot or from errorNote property
     */
    getErrorNote() {
        return html `
      <div part="error-note" class="cat-c-errornote cat-c-fieldset-errornote-part">
        <cat-icon-warning-diamond-filled
          part="error-icon"
          exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
          color="error"
          .iconTitle=${this.errorIconTitle}
          class="cat-c-fieldset__error-icon"
        ></cat-icon-warning-diamond-filled>
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          ?isDisabled=${this.disabled}
          id=${ifDefined(this.errorNote || this.hasErrorNoteSlot ? this.ariaDescribedBy : undefined)}
          .status=${'error'}
        >
          <slot part="part-error" name="error">${this.errorNote}</slot>
        </cat-field-note>
      </div>
    `;
    }
    /**
     * Display warning note from slot or from warningNote property
     */
    getWarningNote() {
        return html `
      <div part="warning-note" class="cat-c-errornote cat-c-fieldset-warningnote-part">
        <cat-icon-warning-triangle-filled
          part="warning-icon"
          exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
          color="knockout-brand"
          class="cat-c-fieldset__error-icon"
          .iconTitle=${this.warningIconTitle}
        ></cat-icon-warning-triangle-filled>
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          ?isDisabled=${this.disabled}
          id=${ifDefined(this.warningNote || this.hasWarningNoteSlot ? this.ariaDescribedBy : undefined)}
          .status=${'warning'}
        >
          <slot part="part-warning" name="warning">${this.warningNote}</slot>
        </cat-field-note>
      </div>
    `;
    }
    /**
     * 1) Display field note from slot or from fieldNote property
     */
    getFieldNote() {
        /* 1 */
        return html `
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        ?isDisabled=${this.disabled}
        id=${ifDefined(this.fieldNote || this.hasFieldNoteSlot ? this.ariaDescribedBy : undefined)}
        class="cat-c-fieldset-fieldnote-slot"
      >
        <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
      </cat-field-note>
    `;
    }
    hasFieldNote() {
        return this.fieldNote || this.hasFieldNoteSlot || this.errorNote || this.warningNote || this.hasErrorNoteSlot || this.hasWarningNoteSlot;
    }
    render() {
        const componentClassNames = this.componentClassNames('cat-c-fieldset', {
            'cat-c-fieldset--inverted': this.inverted === true,
            'cat-c-fieldset--required': this.required,
            'cat-c-fieldset--disabled': this.disabled === true,
            'cat-c-fieldset--horizontal': this.orientation === 'horizontal',
            'cat-c-fieldset--has-error': this.hasErrorNoteSlot || (this.status === 'error' && this.errorNote),
            'cat-c-fieldset--has-warning': this.hasWarningNoteSlot || (this.status === 'warning' && this.warningNote),
            'cat-c-fieldset--has-field-note': this.hasFieldNoteSlot || (this.status !== 'error' && this.status !== 'warning' && this.fieldNote)
        });
        this.setAriaDescribedBy();
        return html ` <fieldset
      part="base"
      class="${componentClassNames}"
      aria-describedby="${ifDefined(this.ariaDescribedBy)}"
      aria-labelledby="${ifDefined(this.ariaLabelledBy)}"
    >
      <legend
        part="legend"
        id="${ifDefined(this.ariaLabelledBy)}"
        class=${this.hideLegend || !this.label ? 'cat-c-fieldset__legend cat-u-is-vishidden' : 'cat-c-fieldset__legend'}
      >
        ${this.label} ${this.required ? html `<span part="part-required" class="cat-c-fieldset__required">${this.notation}</span>` : nothing}
      </legend>

      <div part="body" class="cat-c-fieldset__body">
        <div
          part="list"
          role="${ifDefined(this.fieldsetRole)}"
          aria-label=${ifDefined(this.arialabel)}
          class="cat-c-fieldset__list"
          aria-multiselectable="${ifDefined(this.fieldsetRole === 'listbox' ? 'true' : undefined)}"
        >
          <slot part="part-default"></slot>
        </div>
      </div>
      <!--- field note -->
      ${this.getFieldNote()}
      <!--- error note -->
      ${this.getErrorNote()}
      <!--- warning note -->
      ${this.getWarningNote()}
    </fieldset>`;
    }
}
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "status", void 0);
__decorate([
    property({ type: Boolean })
], BlocksFieldset.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "fieldNote", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "errorNote", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "warningNote", void 0);
__decorate([
    property({ type: Boolean })
], BlocksFieldset.prototype, "required", void 0);
__decorate([
    property({ type: Boolean })
], BlocksFieldset.prototype, "hideLegend", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "notation", void 0);
__decorate([
    property({ type: Boolean })
], BlocksFieldset.prototype, "inverted", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "ariaDescribedBy", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "ariaLabelledBy", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "label", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "arialabel", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "orientation", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "warningIconTitle", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "errorIconTitle", void 0);
__decorate([
    queryAssignedNodes({ flatten: true })
], BlocksFieldset.prototype, "checkboxFieldItems", void 0);
__decorate([
    property({ type: String })
], BlocksFieldset.prototype, "fieldsetRole", void 0);
if (customElements.get('cat-fieldset') === undefined) {
    customElements.define('cat-fieldset', BlocksFieldset);
}
